import { useHirerAuth } from '@seek/hirer-auth-react';
import RoleRequirementsWidget, {
  type Widget,
  WorkType,
  type RoleRequirementsEvent,
  extractQuestionnaireId,
} from '@seek/rr-caja';
import type { UnifiedAnalyticsPayload } from '@seek/rr-caja/src/analytics/types';
import { useLanguage, useTranslations } from '@vocab/react';
import React, {
  type FunctionComponent,
  type ReactNode,
  type Ref,
  useCallback,
} from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';

import { LANGUAGE_DEFAULT } from 'src/app/shared/constants/language';
import {
  useFeatureToggleData,
  FEATURE_CART_AND_PAY_AND_POST_V2,
} from 'src/app/shared/featureToggle';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import {
  hotJarTrigger,
  isHotJarEnabled,
} from 'src/app/shared/hotJar/hotJarTrigger';
import { getProductFeatureByType } from 'src/app/utils/getProductFeatureByType';
import { getRoleRequirementsConfig } from 'src/app/utils/getRoleRequirementsConfig';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { actionUpdateSelectedRRQuestions } from 'src/app/v1/state/modules/session/selectedRRQuestions';
import { useIsApplyViaSeek } from 'src/app/v1/views/hooks/useIsApplyViaSeek';
import { PageSection } from 'src/app/v1/views/sections/PageSection';
import type { AppState } from 'src/types/appState';
import { CajaWorkType } from 'src/types/cajaWorkType';

import translations from './.vocab';
import { ContactUs } from './components/ContactUs';
import { NotAvailable } from './components/NotAvailable';

const PARTNER_ID = 'SEEK-Australia';

const convertCajaWorkType = (cajaWorkType: CajaWorkType): WorkType => {
  switch (cajaWorkType) {
    case CajaWorkType.PartTime:
      return WorkType.PartTime;
    case CajaWorkType.Contract:
      return WorkType.Contract;
    case CajaWorkType.Casual:
      return WorkType.Temporary;
    default:
      return WorkType.FullTime;
  }
};

interface Props {
  rrWidgetRef: Ref<Widget>;
}

export const RoleRequirements: FunctionComponent<Props> = ({
  rrWidgetRef,
}: Props) => {
  const {
    advertiserId,
    classificationId,
    jobLocationCountry,
    jobDescription,
    jobTitle,
    locationId,
    productFeatures,
    questionnaireUrl,
    subclassificationId,
    workType,
    flowType,
  } = useSelector(
    (state: AppState) => ({
      advertiserId: state.session.advertiserId,
      classificationId: state.session.seekClassificationId,
      jobLocationCountry: state.session.jobLocationCountry,
      jobDescription: state.formData.jobDescription,
      jobTitle: state.formData.jobTitle,
      locationId: state.formData.jobLocationId,
      productFeatures: state.session.productFeatures,
      questionnaireUrl: state.formData.questionnaireUrl,
      subclassificationId: state.session.seekSubClassificationId,
      workType: state.formData.workType,
      flowType: state.formData.flowType,
    }),
    shallowEqual,
  );

  const { t } = useTranslations(translations);
  const dispatch = useDispatch();

  const { cartFeatures } = useShoppingCart();

  const isCartEnabled = useFeatureToggleData(FEATURE_CART_AND_PAY_AND_POST_V2);

  const onTrackingCallback = (event: RoleRequirementsEvent) => {
    for (const { payload } of event.data) {
      getTrackingAdapter.sendLinkEvent(
        payload.analytics as UnifiedAnalyticsPayload,
      );
    }
  };

  const languageId = useLanguage().language ?? LANGUAGE_DEFAULT;
  const rrMandatoryQuestionsFeature = getProductFeatureByType({
    featureType: 'RoleRequirementsMandatoryQuestions',
    productFeatures: isCartEnabled ? cartFeatures : productFeatures,
  });

  // use static data for edit flow to prevent RR widget re-rendering multiple times on edit page
  const jobDetailsFromState =
    flowType === 'edit'
      ? {
          partnerId: PARTNER_ID,
          countryId: '',
          languageId,
          locationId: '',
          jobTitle: '',
          classificationId: '',
          subclassificationId: '',
          workTypeId: WorkType.FullTime,
        }
      : {
          partnerId: PARTNER_ID,
          countryId: jobLocationCountry !== '' ? jobLocationCountry : 'zz', // Send default code if country is unknown
          languageId,
          locationId,
          jobText: jobDescription,
          jobTitle,
          classificationId,
          subclassificationId,
          workTypeId: convertCajaWorkType(workType),
          mandatoryQuestion: rrMandatoryQuestionsFeature && {
            questionAnswerSet: rrMandatoryQuestionsFeature.questionAnswerSet,
          },
        };

  const { getToken } = useHirerAuth();
  const roleRequirementsConfig = getRoleRequirementsConfig(
    getToken,
    advertiserId,
    languageId,
  );

  const isApplyViaSeek = useIsApplyViaSeek();

  const EditModeContainer = (children: ReactNode) => <>{children}</>;

  const showHeader = flowType === 'create';

  const onSelectedQuestionsChange = useCallback(
    (questions: string[]) => {
      dispatch(actionUpdateSelectedRRQuestions(questions));
    },
    [dispatch],
  );

  const renderNotAvailable = () =>
    showHeader ? (
      <PageSection heading={t('role-requirements-page-section')}>
        <NotAvailable />
      </PageSection>
    ) : (
      <NotAvailable />
    );

  return isApplyViaSeek ? (
    <RoleRequirementsWidget
      config={roleRequirementsConfig}
      questionnaireId={extractQuestionnaireId(questionnaireUrl) ?? ''}
      triggerHotjarEvent={isHotJarEnabled() ? hotJarTrigger : undefined}
      onSelectedQuestionsChange={
        flowType === 'create' ? onSelectedQuestionsChange : undefined
      }
      jobDetails={jobDetailsFromState}
      helpInformation={<ContactUs />}
      ref={rrWidgetRef}
      onEvent={onTrackingCallback}
      features={{
        showHeader,
        unifiedAnalytics: true,
      }}
      renderQuestionsTile={
        flowType === 'create' ? undefined : EditModeContainer
      }
    />
  ) : (
    renderNotAvailable()
  );
};
